import React, { FC, useEffect } from 'react';

import { SpecializationDto } from '@apiModels/SpecializationDto.csharp';
import { Combobox } from '../Partials/Combobox';
import { PartialsBackgroundVariants } from '../Partials/PartialsBackgroundVariants';

import { useInsuranceFilters } from '../Utils/Context/filtersContext';
import { useInsuranceLabels } from '../Utils/Context/labelsContext';
import { filterSpecializations } from '../Utils/Filters/filterSpecializations';
import { UrlHandler } from '../Utils/UrlHandler';

interface SpecializationsComboboxProps {
  specializations: SpecializationDto[];
  getDataFromUrl?: boolean
  variant?: PartialsBackgroundVariants
}

export const SpecializationsCombobox: FC<SpecializationsComboboxProps> = (props) => {
  const { specializations, getDataFromUrl = true, variant } = props;
  
  const {
    locations,
    specialization,
    setSpecialization,
    setService,
  } = useInsuranceFilters();
  
  const {
    inputs
  } = useInsuranceLabels();

  const filteredSpecializations = filterSpecializations({
    locations
  }, specializations)

  useEffect(() => {
    if (getDataFromUrl) {
      const foundSpecialization = UrlHandler.getEntryFromUrlQuery<SpecializationDto>(specializations, 'specializationId');
      foundSpecialization && setSpecialization(foundSpecialization as SpecializationDto);
    }
  }, [])

  useEffect(() => {
    if (filteredSpecializations && filteredSpecializations.length === 1) {
      const [singleSpecialization] = filteredSpecializations;
      if (singleSpecialization.id !== specialization?.id) {
        selectionChange(singleSpecialization);
      }
    }
  }, [locations])

  const keyWordsMatcher = (keyword: string, inputValue: string) =>
    keyword.toLowerCase().includes(inputValue.toLowerCase());

  const inputChange = (specialization: SpecializationDto, inputValue?: string) => {
    if (!inputValue) return true;
    const nameMatchesInput = specialization.name.toLowerCase().startsWith(inputValue.toLowerCase());
    const keywordsMatchInput = !!specialization.keywords
      .filter(keyword => keyWordsMatcher(keyword, inputValue)).length;

    return nameMatchesInput || keywordsMatchInput;
  }

  const selectionChange = (specialization: SpecializationDto | null) => {
    if (!specialization) {
      setSpecialization(null)
    }

    setService(null);
    setSpecialization(specialization)
  }

  const specializationSort = (specializations: SpecializationDto[], inputValue?: string): SpecializationDto[] => {
    if (!inputValue) return specializations;

    const specializationMatchesKeyword = (specialization: SpecializationDto) => !!specialization.keywords
      .filter(keyword => keyWordsMatcher(keyword, inputValue)).length

    const hitsWithKeywords = specializations.filter(specialization => (
      specializationMatchesKeyword(specialization)
    ));

    const hitsWithoutKeywords = specializations.filter(specialization => (
      !specializationMatchesKeyword(specialization)
    ));

    return [
      ...hitsWithoutKeywords,
      ...hitsWithKeywords,
    ]
  }

  const renderSpecialization = (spec: SpecializationDto) =>
    <span className="specializationCombobox__item">{spec.name}</span>

  const specializationToString = (spec: SpecializationDto | null) =>
    spec?.name ?? '';

  return (
    <div className="specializationCombobox">
      <Combobox<SpecializationDto>
        label={inputs.specializationsLabel}
        placeholder={inputs.specializationsLabel}
        items={filteredSpecializations}
        currentItem={specialization}
        onSelectionChange={selectionChange}
        onInputValueChange={inputChange}
        renderItemInList={renderSpecialization}
        itemToString={specializationToString}
        onInputValueListModifier={specializationSort}
        className="specializationCombobox__select"
        backgroundVariant={variant}
      />
    </div>
  )
};
